import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { startQrScanner, stopQrScanner } from "@/lib/qrScanner";
import { getDevicePosition } from "@/lib/geo";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ScanLine, LogIn, LogOut, AlertTriangle, X, Maximize, Camera } from "lucide-react";

// --- Retours sonores (Web Audio, aucun fichier requis) ---------------------
let _audioCtx = null;
const _ctx = () => (_audioCtx = _audioCtx || new (window.AudioContext || window.webkitAudioContext)());
const _tone = (freq, start, dur, type = "sine", gain = 0.25) => {
  const ctx = _ctx();
  const o = ctx.createOscillator();
  const g = ctx.createGain();
  o.type = type; o.frequency.value = freq;
  g.gain.setValueAtTime(gain, ctx.currentTime + start);
  g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + start + dur);
  o.connect(g); g.connect(ctx.destination);
  o.start(ctx.currentTime + start); o.stop(ctx.currentTime + start + dur);
};
const playEntry = () => { _tone(880, 0, 0.15); _tone(1320, 0.12, 0.2); };
const playExit = () => { _tone(660, 0, 0.15); _tone(440, 0.12, 0.25); };
const playError = () => { _tone(180, 0, 0.5, "square", 0.3); };

const fmtTime = (iso) => new Date(iso).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });

// --- Horloge grand format ---------------------------------------------------
const Clock = () => {
  const [now, setNow] = useState(new Date());
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(t); }, []);
  return (
    <div className="text-center" data-testid="kiosque-clock">
      <div className="text-6xl sm:text-7xl font-extrabold tabular-nums text-white tracking-tight">
        {now.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
      </div>
      <div className="text-slate-400 text-lg capitalize mt-1">
        {now.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
      </div>
    </div>
  );
};

// --- Écran de résultat géant -----------------------------------------------
const KioskResult = ({ result }) => {
  if (!result) return null;
  if (result.error) {
    return (
      <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-red-600 animate-in fade-in duration-200" data-testid="kiosque-result-error">
        <AlertTriangle className="h-32 w-32 text-white mb-6" />
        <div className="text-4xl sm:text-5xl font-extrabold text-white text-center px-8">BADGE NON RECONNU</div>
        <div className="text-xl text-red-100 mt-4 text-center px-8">{result.error}</div>
      </div>
    );
  }
  if (result.staff) {
    const isEntry = result.action === "entree";
    return (
      <div className={`fixed inset-0 z-50 flex flex-col items-center justify-center animate-in fade-in duration-200 ${isEntry ? "bg-blue-700" : "bg-slate-700"}`} data-testid="kiosque-result-staff">
        {isEntry ? <LogIn className="h-32 w-32 text-white mb-6" /> : <LogOut className="h-32 w-32 text-white mb-6" />}
        <div className="text-3xl font-bold text-white/80 uppercase tracking-widest">Personnel — {isEntry ? "Entrée" : "Sortie"}</div>
        <div className="text-5xl sm:text-6xl font-extrabold text-white mt-4 text-center px-8">{result.employee.name}</div>
        <div className="text-2xl text-white/70 mt-4 tabular-nums">{fmtTime(result.time)}</div>
      </div>
    );
  }
  const { movement, student } = result;
  const isEntry = movement.type === "entree";
  const alert = movement.flag === "sortie_anticipee_non_autorisee";
  const bg = alert ? "bg-red-600" : isEntry ? "bg-green-600" : "bg-orange-500";
  const photo = student?.photo_base64
    ? (student.photo_base64.startsWith("data:") ? student.photo_base64 : `data:image/jpeg;base64,${student.photo_base64}`)
    : null;
  return (
    <div className={`fixed inset-0 z-50 flex flex-col items-center justify-center animate-in fade-in duration-200 ${bg}`} data-testid="kiosque-result-student">
      {photo ? (
        <img src={photo} alt={movement.student_name}
          className="h-44 w-44 rounded-full object-cover border-8 border-white/40 shadow-2xl mb-6"
          data-testid="kiosque-student-photo" />
      ) : alert ? <AlertTriangle className="h-32 w-32 text-white mb-6" />
        : isEntry ? <LogIn className="h-32 w-32 text-white mb-6" /> : <LogOut className="h-32 w-32 text-white mb-6" />}
      <div className="text-3xl font-bold text-white/80 uppercase tracking-widest">
        {alert ? "SORTIE NON AUTORISÉE !" : isEntry ? "Entrée enregistrée" : "Sortie enregistrée"}
      </div>
      <div className="text-5xl sm:text-6xl font-extrabold text-white mt-4 text-center px-8">{movement.student_name}</div>
      <div className="text-2xl text-white/80 mt-3">{movement.class_name} · {movement.matricule}</div>
      <div className="text-2xl text-white/70 mt-2 tabular-nums">{fmtTime(movement.timestamp)}</div>
      {alert && <div className="text-xl font-bold text-white mt-6 bg-red-800/60 px-6 py-3 rounded-2xl">Aucune autorisation aujourd&apos;hui — le parent est alerté.</div>}
    </div>
  );
};

export default function KiosquePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [scanning, setScanning] = useState(false);
  const [camError, setCamError] = useState("");
  const [result, setResult] = useState(null);
  const [manual, setManual] = useState("");
  const [summary, setSummary] = useState(null);
  const qrRef = useRef(null);
  const coolRef = useRef(0);
  const busyRef = useRef(false);
  const hideTimer = useRef(null);
  const wakeRef = useRef(null);

  const loadSummary = useCallback(async () => {
    try { const { data } = await api.get("/gate/summary"); setSummary(data); } catch { /* ignore */ }
  }, []);

  const showResult = (res) => {
    setResult(res);
    clearTimeout(hideTimer.current);
    hideTimer.current = setTimeout(() => setResult(null), 4000);
  };

  const doScan = useCallback(async (code) => {
    if (busyRef.current || !code) return;
    busyRef.current = true;
    try {
      const gps = await getDevicePosition();
      const { data } = await api.post("/gate/scan", { code, direction: "auto", ...(gps || {}) });
      if (data.staff) { data.action === "entree" ? playEntry() : playExit(); }
      else if (data.movement?.flag === "sortie_anticipee_non_autorisee") playError();
      else data.movement?.type === "entree" ? playEntry() : playExit();
      showResult(data);
      loadSummary();
    } catch (e) {
      playError();
      showResult({ error: e.response?.data?.detail || "Erreur de scan — réessayez" });
    } finally { busyRef.current = false; }
  }, [loadSummary]);

  const startCamera = useCallback(async () => {
    setCamError("");
    const onDecoded = (text) => {
      const now = Date.now();
      if (now - coolRef.current < 3000) return;
      coolRef.current = now;
      doScan(text);
    };
    try {
      qrRef.current = await startQrScanner("kiosque-qr-reader", onDecoded);
      setScanning(true);
    } catch (e) {
      setCamError(e.message);
    }
  }, [doScan]);

  // Anti-veille de la tablette + synthèse au montage
  useEffect(() => {
    if (!user) return undefined;
    loadSummary();
    const askWakeLock = async () => {
      try { wakeRef.current = await navigator.wakeLock?.request("screen"); } catch { /* non supporté */ }
    };
    askWakeLock();
    const onVis = () => { if (document.visibilityState === "visible") askWakeLock(); };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      wakeRef.current?.release?.();
      clearTimeout(hideTimer.current);
      stopQrScanner(qrRef.current);
    };
  }, [loadSummary, user]);

  const goFullscreen = () => {
    document.documentElement.requestFullscreen?.().catch(() => {});
  };

  const exitKiosk = async () => {
    await stopQrScanner(qrRef.current);
    document.exitFullscreen?.().catch?.(() => {});
    navigate("/dashboard/portail");
  };

  if (!user) {
    return <Navigate to="/admin-portal" replace />;
  }

  return (
    <div className="min-h-screen bg-[#0B1220] flex flex-col" data-testid="kiosque-page">
      {/* Barre discrète */}
      <div className="flex items-center justify-between px-4 py-2">
        <div className="text-slate-500 text-xs font-bold uppercase tracking-widest flex items-center gap-2">
          <ScanLine className="h-3.5 w-3.5" /> Mode kiosque — Portail
        </div>
        <div className="flex gap-1">
          <button onClick={goFullscreen} className="p-2 rounded-lg text-slate-500 hover:text-white hover:bg-white/10" title="Plein écran" data-testid="kiosque-fullscreen-btn">
            <Maximize className="h-4 w-4" />
          </button>
          <button onClick={exitKiosk} className="p-2 rounded-lg text-slate-500 hover:text-white hover:bg-white/10" title="Quitter le mode kiosque" data-testid="kiosque-exit-btn">
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="flex-1 flex flex-col items-center justify-center gap-6 px-4 pb-6">
        <Clock />

        {/* Compteurs du jour */}
        {summary && (
          <div className="grid grid-cols-3 gap-3 w-full max-w-md" data-testid="kiosque-summary">
            {[["Entrés", summary.entres, "text-green-400"], ["Présents", summary.presents, "text-blue-400"], ["Sortis", summary.sortis, "text-orange-400"]].map(([l, v, cls]) => (
              <div key={l} className="bg-white/5 rounded-2xl p-3 text-center border border-white/10">
                <div className={`text-3xl font-extrabold tabular-nums ${cls}`}>{v}</div>
                <div className="text-[11px] uppercase text-slate-400 tracking-wider">{l}</div>
              </div>
            ))}
          </div>
        )}

        {/* Zone caméra */}
        <div className="w-full max-w-md">
          <div className="rounded-3xl overflow-hidden border-2 border-white/15 bg-black relative">
            <div id="kiosque-qr-reader" />
            {!scanning && (
              <div className="py-16 text-center">
                <Camera className="h-12 w-12 mx-auto text-slate-600 mb-4" />
                {camError && <p className="text-red-400 text-sm px-6 mb-4" data-testid="kiosque-cam-error">{camError}</p>}
                <Button onClick={() => { startCamera(); goFullscreen(); }} className="bg-[#166534] hover:bg-[#14532d] text-white text-lg px-8 py-6 rounded-2xl gap-2" data-testid="kiosque-start-btn">
                  <Camera className="h-5 w-5" /> Démarrer le scan
                </Button>
              </div>
            )}
          </div>
          {scanning && (
            <p className="text-center text-slate-400 text-lg mt-4 animate-pulse" data-testid="kiosque-ready">
              Présentez votre carte ou badge devant la caméra
            </p>
          )}
        </div>

        {/* Saisie secours (matricule) */}
        <form onSubmit={(e) => { e.preventDefault(); doScan(manual.trim()); setManual(""); }} className="flex gap-2 w-full max-w-md">
          <Input value={manual} onChange={(e) => setManual(e.target.value)} placeholder="Saisie secours : matricule ou code badge"
            className="bg-white/5 border-white/15 text-white placeholder:text-slate-500 font-mono h-12" data-testid="kiosque-manual-input" />
          <Button type="submit" disabled={!manual.trim()} className="bg-[#1E3A8A] text-white h-12 px-6" data-testid="kiosque-manual-btn">
            <ScanLine className="h-5 w-5" />
          </Button>
        </form>
      </div>

      <KioskResult result={result} />
    </div>
  );
}
